import shoelaceStyles from '../styles/shoelaceStyles';
import styles from '../styles/styles.scss';
import {createRef, Ref, ref} from 'lit/directives/ref.js';
import {css, html, LitElement} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {Feature} from 'geojson';
import {ConfigurationState} from '../storage/ConfigurationState';
import {when} from 'lit/directives/when.js';
import {Site} from '../models/Site';
import SlDrawer from '@shoelace-style/shoelace/dist/components/drawer/drawer.js';
import {log} from '../controllers/LogController';
import {AnalyticsController} from '../controllers/AnalyticsController';

// prevent tree shaking
export {};

@customElement('ca-detail')
export class CaDetailElement extends LitElement {
  static override styles = [
    shoelaceStyles,
    styles,
    css`
      :host {
        display: block;
      }

      sl-drawer {
        --size: calc(clamp(500px, 40vw, 100vw));
      }

      sl-drawer::part(body) {
        display: flex;
        flex-direction: column;
        gap: 1em;
      }

      .box {
        background: var(--sl-color-neutral-0-2nd);
        /*color: var(--sl-color-neutral-800);*/
        padding: 1.5em;
        display: flex;
        gap: 1em;
      }

      .box > svg {
        width: 40px;
        height: 40px;
        color: #fff;
        background: var(--ca-color-highlight);
        flex-shrink: 0;
        padding: 10px;
      }

      .box > div {
        width: 100%;
      }

      a {
        text-decoration: none;
        color: var(--ca-color-highlight);
        font-weight: 500;
      }

      a > svg {
        padding-right: 0.3em;
        vertical-align: middle;
      }

      a:hover {
        color: var(--ca-color-primary);
      }

      .socialmedias {
        display: flex;
        gap: 0.7em;
      }

      .socialmedia-icon {
        width: 30px;
        height: 30px;
        background: var(--sl-color-neutral-0-3rd);
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 5px;
      }

      .socialmedia-icon svg {
        width: 100%;
      }

      .screenshot {
        max-width: calc(min(100%, 960px));
        height: auto;
      }
    `,
  ];

  @property({type: Object})
  feature: Feature | null = null;

  private _drawerRef: Ref<SlDrawer> = createRef();

  override render() {
    return html`
      <sl-drawer
        ${ref(this._drawerRef)}
        ?open=${this.feature !== null}
        @sl-show=${() => this._drawerRef.value?.shadowRoot?.querySelector('slot[part=body]')?.scrollTo(0, 0)}
        @sl-after-hide=${() => this._handleSlAfterHide()}
      >
        ${when(this.feature !== null, () => this._renderDetails())}
      </sl-drawer>
    `;
  }

  closeDrawer() {
    this._drawerRef.value?.hide();
  }

  private _renderDetails() {
    return [
      html`
        <div>
          ${when(
            this.feature?.properties?.hasImage || this.feature?.properties?.screenshot_path,
            () => html`
              <img src="${ConfigurationState.siteImgUrl(this.feature!, 'w960')}"
                   width="960" height="540"
                   alt="Screenshot ${this.feature?.properties?.company}"
                   class="screenshot"
                   loading="lazy"
              >
            `,
          )}
        </div>
      `,
      html`
        <div class="box">
          <svg xmlns="http://www.w3.org/2000/svg" width="24.988" height="20.444" viewBox="0 0 24.988 20.444">
            <path id="Pfad_111" data-name="Pfad 111"
                  d="M23.716,21.173h2.272v2.272H1V21.173H3.272V4.136A1.136,1.136,0,0,1,4.407,3H15.766A1.136,1.136,0,0,1,16.9,4.136V21.173h4.543V12.087H19.173V9.815h3.407a1.136,1.136,0,0,1,1.136,1.136ZM5.543,5.272v15.9H14.63V5.272Zm2.272,6.815h4.543v2.272H7.815Zm0-4.543h4.543V9.815H7.815Z"
                  transform="translate(-1 -3)" fill="currentColor" />
          </svg>
          <div>
            <h2>${this.feature?.properties?.company}</h2>

            <p>
              ${this.feature?.properties?.description}
            </p>

            <a href="${this.feature?.properties?.site_url}"
               rel="noopener noreferrer"
               target="_blank"
               @click=${this._handleOutgoingLinkClick}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
                <path id="Pfad_118" data-name="Pfad 118"
                      d="M10,6V8H5V19H16V14h2v6a1,1,0,0,1-1,1H4a1,1,0,0,1-1-1V7A1,1,0,0,1,4,6ZM21,3v8H19V6.413l-7.793,7.794L9.793,12.793,17.585,5H13V3Z"
                      transform="translate(-3 -3)" fill="currentColor" />
              </svg>

              ${Site.urlDomain(this.feature?.properties?.site_url)}
            </a>
          </div>
        </div>
      `,
      html`
        <div class="box">
          <svg xmlns="http://www.w3.org/2000/svg" width="20.711" height="25" viewBox="0 0 20.711 25">
            <path id="Pfad_112" data-name="Pfad 112"
                  d="M13.355,23.746l5.7-5.7a8.054,8.054,0,1,0-11.39,0Zm0,3.254L6.033,19.678a10.355,10.355,0,1,1,14.645,0Zm0-12.343a2.3,2.3,0,1,0-2.3-2.3A2.3,2.3,0,0,0,13.355,14.657Zm0,2.3a4.6,4.6,0,1,1,4.6-4.6A4.6,4.6,0,0,1,13.355,16.958Z"
                  transform="translate(-3 -2)" fill="currentColor" />
          </svg>

          <div>
            <h3>Adresse</h3>
            ${
              when(
                this.feature?.properties?.address,
                () => html`${this.feature?.properties?.address}<br>`,
              )
            }
            ${
              when(
                `${this.feature?.properties?.zip}${this.feature?.properties?.city}`,
                () => html`${this.feature?.properties?.zip} ${this.feature?.properties?.city}<br>`,
              )
            }
            <a href=""
               @click=${(ev: Event) => {
                 this.dispatchEvent(
                   new CustomEvent(
                     'ca-map-feature-requested',
                     {detail: this.feature},
                   ),
                 );
                 ev.preventDefault();
               }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
                <path id="Pfad_120" data-name="Pfad 120"
                      d="M2,5,9,2l6,3,6.3-2.7a.5.5,0,0,1,.7.46V19l-7,3L9,19,2.7,21.7a.5.5,0,0,1-.7-.46ZM16,19.4l4-1.714V5.033L16,6.747Zm-2-.132V6.736l-4-2V17.264ZM8,17.253V4.6L4,6.319V18.967Z"
                      transform="translate(-2 -2)" fill="currentColor" />
              </svg>

              Auf der Karte anzeigen
            </a>
          </div>
        </div>
      `,
      when(
        this.feature?.properties?.social_media_url_twitter
        || this.feature?.properties?.social_media_url_facebook
        || this.feature?.properties?.social_media_url_xing
        || this.feature?.properties?.social_media_url_linkedin
        || this.feature?.properties?.social_media_url_tiktok
        || this.feature?.properties?.social_media_url_instagram
        || this.feature?.properties?.social_media_url_youtube,
        () => html`
          <div class="box">
            <svg xmlns="http://www.w3.org/2000/svg" width="25" height="23.863" viewBox="0 0 25 23.863">
              <path id="Pfad_113" data-name="Pfad 113"
                    d="M21.318,7.818H27v2.273H21.318ZM19.045,13.5H27v2.273H19.045Zm3.409,5.682H27v2.273H22.455ZM2,24.864a9.091,9.091,0,0,1,18.182,0H17.909a6.818,6.818,0,1,0-13.636,0Zm9.091-10.227a6.818,6.818,0,1,1,6.818-6.818A6.816,6.816,0,0,1,11.091,14.636Zm0-2.273A4.545,4.545,0,1,0,6.545,7.818,4.544,4.544,0,0,0,11.091,12.364Z"
                    transform="translate(-2 -1)" fill="currentColor" />
            </svg>

            <div>
              <h3>Social Media Kanäle</h3>
              <div class="socialmedias">
                ${when(
                  this.feature?.properties?.social_media_url_twitter,
                  () => html`
                    <a href="${this.feature?.properties?.social_media_url_twitter}"
                       class="socialmedia-icon"
                       rel="noopener noreferrer"
                       target="_blank"
                       title="Twitter/X"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="21.573" height="19.5" viewBox="0 0 21.573 19.5">
                        <path id="Pfad_28" data-name="Pfad 28"
                              d="M18.2,2.25h3.308l-7.227,8.26,8.5,11.24H16.131l-5.214-6.817L4.951,21.75H1.641l7.73-8.835L1.215,2.25H8.041l4.713,6.231ZM17.044,19.77h1.833L7.045,4.126H5.078Z"
                              transform="translate(-1.215 -2.25)" fill="currentColor" />
                      </svg>
                    </a>
                  `,
                )}
                ${when(
                  this.feature?.properties?.social_media_url_facebook,
                  () => html`
                    <a href="${this.feature?.properties?.social_media_url_facebook}"
                       class="socialmedia-icon"
                       rel="noopener noreferrer"
                       target="_blank"
                       title="Facebook"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="10.5" height="20" viewBox="0 0 10.5 20">
                        <path id="Pfad_27" data-name="Pfad 27"
                              d="M14,13.5h2.5l1-4H14v-2c0-1.029,0-2,2-2h1.5V2.14A28.074,28.074,0,0,0,14.643,2,4.336,4.336,0,0,0,10,6.7V9.5H7v4h3V22h4Z"
                              transform="translate(-7 -2)" fill="currentColor" />
                      </svg>
                    </a>
                  `,
                )}
                ${when(
                  this.feature?.properties?.social_media_url_linkedin,
                  () => html`
                    <a href="${this.feature?.properties?.social_media_url_linkedin}"
                       class="socialmedia-icon"
                       rel="noopener noreferrer"
                       target="_blank"
                       title="LinkedIn"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="19.061" height="18.001"
                           viewBox="0 0 19.061 18.001">
                        <path id="Pfad_24" data-name="Pfad 24"
                              d="M6.94,5A2,2,0,1,1,5,3,2,2,0,0,1,6.94,5ZM7,8.48H3V21H7Zm6.32,0H9.34V21h3.94V14.43c0-3.66,4.77-4,4.77,0V21H22V13.07c0-6.17-7.06-5.94-8.72-2.91Z"
                              transform="translate(-2.94 -2.999)" fill="currentColor" />
                      </svg>
                    </a>
                  `,
                )}
                ${when(
                  this.feature?.properties?.social_media_url_xing,
                  () => html`
                    <a href="${this.feature?.properties?.social_media_url_xing}"
                       class="socialmedia-icon"
                       rel="noopener noreferrer"
                       target="_blank"
                       title="Xing"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16.192" height="19.076"
                           viewBox="0 0 16.192 19.076">
                        <path id="Pfad_25" data-name="Pfad 25"
                              d="M20.462,3.231a.587.587,0,0,1,.385.154.49.49,0,0,1,0,.462L14.77,14.615l3.846,7.077a.49.49,0,0,1,0,.461.588.588,0,0,1-.385.154H15.462a.973.973,0,0,1-.769-.538L10.77,14.615C11,14.308,16.924,3.769,16.924,3.769a.836.836,0,0,1,.769-.538ZM8.923,7a.973.973,0,0,1,.769.538l1.923,3.308c-.154.154-3,5.231-3,5.231a.973.973,0,0,1-.769.538H5.154a.588.588,0,0,1-.385-.154.49.49,0,0,1,0-.462l2.846-5.154L5.77,7.615a.49.49,0,0,1,0-.462A.588.588,0,0,1,6.154,7Z"
                              transform="translate(-4.712 -3.23)" fill="currentColor" />
                      </svg>
                    </a>
                  `,
                )}
                ${when(
                  this.feature?.properties?.social_media_url_instagram,
                  () => html`
                    <a href="${this.feature?.properties?.social_media_url_instagram}"
                       class="socialmedia-icon"
                       rel="noopener noreferrer"
                       target="_blank"
                       title="Instagram"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
                        <path id="Pfad_57" data-name="Pfad 57"
                              d="M12,9a3,3,0,1,0,3,3A3,3,0,0,0,12,9Zm0-2a5,5,0,1,1-5,5A5,5,0,0,1,12,7Zm6.5-.251A1.25,1.25,0,1,1,17.251,5.5,1.251,1.251,0,0,1,18.5,6.749ZM12,4c-2.475,0-2.878.007-4.029.058a5.44,5.44,0,0,0-1.8.332,2.9,2.9,0,0,0-1.081.7,2.89,2.89,0,0,0-.7,1.081,5.419,5.419,0,0,0-.332,1.8C4.007,9.075,4,9.461,4,12c0,2.474.007,2.878.058,4.029a5.444,5.444,0,0,0,.332,1.8,3.11,3.11,0,0,0,1.782,1.783,5.444,5.444,0,0,0,1.8.333c1.1.052,1.49.058,4.029.058,2.474,0,2.878-.007,4.029-.058a5.46,5.46,0,0,0,1.8-.332,3.11,3.11,0,0,0,1.784-1.782,5.442,5.442,0,0,0,.332-1.8c.052-1.1.058-1.49.058-4.029,0-2.474-.007-2.878-.058-4.029a5.46,5.46,0,0,0-.332-1.8,2.912,2.912,0,0,0-.7-1.081,2.884,2.884,0,0,0-1.081-.7,5.423,5.423,0,0,0-1.8-.332C14.926,4.006,14.54,4,12,4Zm0-2c2.717,0,3.056.01,4.123.06a7.357,7.357,0,0,1,2.427.465,4.872,4.872,0,0,1,1.772,1.153A4.908,4.908,0,0,1,21.476,5.45a7.4,7.4,0,0,1,.465,2.428C21.989,8.944,22,9.283,22,12s-.01,3.056-.06,4.122a7.381,7.381,0,0,1-.465,2.427,5.111,5.111,0,0,1-2.925,2.925,7.4,7.4,0,0,1-2.427.465c-1.067.047-1.406.06-4.123.06s-3.056-.01-4.123-.06a7.383,7.383,0,0,1-2.427-.465A5.11,5.11,0,0,1,2.526,18.55a7.378,7.378,0,0,1-.465-2.427C2.013,15.056,2,14.717,2,12s.01-3.056.06-4.122A7.352,7.352,0,0,1,2.526,5.45,4.879,4.879,0,0,1,3.679,3.678,4.9,4.9,0,0,1,5.451,2.525,7.374,7.374,0,0,1,7.878,2.06C8.945,2.013,9.284,2,12,2Z"
                              transform="translate(-2.001 -2)" fill="currentColor" />
                      </svg>
                    </a>
                  `,
                )}
                ${when(
                  this.feature?.properties?.social_media_url_tiktok,
                  () => html`
                    <a href="${this.feature?.properties?.social_media_url_tiktok}"
                       class="socialmedia-icon"
                       rel="noopener noreferrer"
                       target="_blank"
                       title="Tiktok"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" viewBox="0 0 18 20">
                        <path id="Pfad_62" data-name="Pfad 62"
                              d="M16,8.245V15.5a6.493,6.493,0,1,1-5-6.326v3.163A3.5,3.5,0,1,0,13,15.5V2h3a5,5,0,0,0,5,5v3A7.966,7.966,0,0,1,16,8.245Z"
                              transform="translate(-3 -2)" fill="currentColor" />
                      </svg>
                    </a>
                  `,
                )}
                ${when(
                  this.feature?.properties?.social_media_url_youtube,
                  () => html`
                    <a href="${this.feature?.properties?.social_media_url_youtube}"
                       class="socialmedia-icon"
                       rel="noopener noreferrer"
                       target="_blank"
                       title="Youtube"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="19.999" height="16" viewBox="0 0 19.999 16">
                        <path id="Pfad_59" data-name="Pfad 59"
                              d="M12.244,4c.534,0,1.87.016,3.29.073l.5.022a20.436,20.436,0,0,1,3.566.381A2.811,2.811,0,0,1,21.542,6.5,29.349,29.349,0,0,1,22,11.836v.327a29.35,29.35,0,0,1-.456,5.338A2.811,2.811,0,0,1,19.6,19.523a20.44,20.44,0,0,1-3.566.381l-.5.022c-1.42.057-2.756.07-3.29.073h-.489a46.835,46.835,0,0,1-7.36-.476A2.811,2.811,0,0,1,2.456,17.5,29.356,29.356,0,0,1,2,12.163v-.327A29.355,29.355,0,0,1,2.456,6.5,2.811,2.811,0,0,1,4.395,4.476,46.829,46.829,0,0,1,11.755,4ZM10,8.5v7L16,12Z"
                              transform="translate(-2 -4)" fill="currentColor" />
                      </svg>
                    </a>
                  `,
                )}
              </div>
            </div>
          </div>
        `,
      ),
      when(
        this.feature?.properties?.tagNames,
        () => html`
          <div class="box">
            <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25">
              <path id="Pfad_114" data-name="Pfad 114"
                    d="M12.82,2.1l11.9,1.7,1.7,11.9L15.37,26.748a1.2,1.2,0,0,1-1.7,0l-11.9-11.9a1.2,1.2,0,0,1,0-1.7Zm.85,2.55L4.321,14l10.2,10.2,9.349-9.349L22.594,5.925ZM16.22,12.3a2.4,2.4,0,1,1,3.4,0A2.4,2.4,0,0,1,16.22,12.3Z"
                    transform="translate(-1.419 -2.101)" fill="currentColor" />
            </svg>

            <div>
              <h3>Keywords</h3>
              ${this.feature?.properties?.tagNames.split(' | ').map(
                (tag: string) => html`
                  <sl-badge>${tag}</sl-badge>
                `,
              )}
            </div>
          </div>
        `,
      ),
    ];
  }

  private _handleSlAfterHide() {
    log(this.constructor.name, 'dispatching ca-feature-selected=null');
    this.dispatchEvent(
      new CustomEvent(
        'ca-feature-selected',
        {
          detail: null,
          bubbles: true,
          composed: true,
        },
      ),
    );
  }

  private _handleOutgoingLinkClick(ev: Event) {
    log(this.constructor.name, '_handleOutgoingLinkClick');
    AnalyticsController.logOutgoingLink(
      this.feature?.properties?.slug,
      this.feature?.properties?.site_url,
    );
  }
}
